import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../i18n/useTranslation';
import { booksellerTestService, type BooksellerDto, type EntitlementApiTestResult } from '../../services/booksellerTestService';
import { useAuth } from '../../hooks/useAuth';
import Layout from '../../components/Layout';

const BooksellerEntitlementTestPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [booksellers, setBooksellers] = useState<BooksellerDto[]>([]);
  const [selectedBooksellerId, setSelectedBooksellerId] = useState<string>('');
  const [testEmail, setTestEmail] = useState<string>('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<EntitlementApiTestResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const preselectedBooksellerId = searchParams.get('bookseller');

  // Available tokens for URL/header/body replacement
  const availableTokens = [
    { token: '{userEmail}', value: user?.email || 'user@example.com', description: 'Logged-in user email' },
    { token: '{email}', value: user?.email || 'user@example.com', description: 'Alias for userEmail' },
    { token: '{userId}', value: user?.sub || 'user-id-123', description: 'Logged-in user ID' },
    { token: '{userName}', value: user?.name || 'John Doe', description: 'Logged-in user full name' },
    { token: '{firstName}', value: user?.given_name || 'John', description: 'User first name' },
    { token: '{lastName}', value: user?.family_name || 'Doe', description: 'User last name' },
    { token: '{booksellerId}', value: selectedBooksellerId || 'bookseller-guid', description: 'Selected bookseller ID' },
  ];

  // Load booksellers on mount
  useEffect(() => {
    loadBooksellers();
  }, []);

  // Auto-populate test email from user
  useEffect(() => {
    if (user?.email && !testEmail) {
      setTestEmail(user.email);
    }
  }, [user]);

  const loadBooksellers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await booksellerTestService.getAllBooksellers();
      setBooksellers(data);
      if (preselectedBooksellerId && data.some((b) => b.id === preselectedBooksellerId)) {
        setSelectedBooksellerId(preselectedBooksellerId);
      } else if (data.length > 0) {
        setSelectedBooksellerId(data[0].id);
      }
    } catch (err: any) {
      console.error('Failed to load booksellers:', err);
      setError(err.message || 'Failed to load booksellers');
    } finally {
      setLoading(false);
    }
  };

  const handleTest = async () => {
    if (!selectedBooksellerId) {
      setError(t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.SelectBooksellerError'));
      return;
    }

    if (!testEmail || !testEmail.includes('@')) {
      setError(t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.InvalidEmailError'));
      return;
    }

    try {
      setTesting(true);
      setError(null);
      setTestResult(null);
      const result = await booksellerTestService.testEntitlementApi(selectedBooksellerId, testEmail);
      setTestResult(result);
    } catch (err: any) {
      console.error('Entitlement API test failed:', err);
      setError(err.message || 'Entitlement API test failed');
      setTestResult({
        success: false,
        errorMessage: err.message || 'Entitlement API test failed',
        responseTimeMs: 0
      });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            {t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.Title')}
          </h1>
          <p className="text-gray-600">
            {t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.Subtitle')}
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-6">
            <p className="text-primary-800">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.LoadingBooksellers')}</p>
          </div>
        )}

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-red-800 mb-1">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.ErrorLabel')}</h3>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Main Content Grid */}
        {!loading && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Test Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Test Form */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TestConfigTitle')}</h2>

                {/* Bookseller Selection */}
                <div className="mb-6">
                  <label htmlFor="bookseller" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.SelectBooksellerLabel')}
                  </label>
                  <select
                    id="bookseller"
                    value={selectedBooksellerId}
                    onChange={(e) => setSelectedBooksellerId(e.target.value)}
                    disabled={testing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    {booksellers.length === 0 && (
                      <option value="">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.NoBooksellerOption')}</option>
                    )}
                    {booksellers.map((bookseller) => (
                      <option key={bookseller.id} value={bookseller.id}>
                        {bookseller.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Test Email Input */}
                <div className="mb-6">
                  <label htmlFor="testEmail" className="block text-sm font-medium text-gray-700 mb-2">
                    {t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TestEmailLabel')}
                  </label>
                  <input
                    type="email"
                    id="testEmail"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder={t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TestEmailPlaceholder')}
                    disabled={testing}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    {t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TestEmailHint')}
                  </p>
                </div>

                {/* Test Button */}
                <button
                  onClick={handleTest}
                  disabled={testing || !selectedBooksellerId || !testEmail || booksellers.length === 0}
                  className="w-full bg-primary-600 hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center"
                >
                  {testing ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TestingButton')}
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                      {t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TestButton')}
                    </>
                  )}
                </button>

                {/* What This Tests */}
                <div className="mt-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                  <h3 className="text-sm font-semibold text-primary-900 mb-2">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.WhatThisTestsTitle')}</h3>
                  <ul className="text-sm text-primary-800 space-y-1 list-disc list-inside">
                    <li>{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TestItem1')}</li>
                    <li>{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TestItem2')}</li>
                    <li>{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TestItem3')}</li>
                    <li>{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TestItem4')}</li>
                    <li>{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TestItem5')}</li>
                    <li>{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TestItem6')}</li>
                  </ul>
                </div>
              </div>

              {/* Test Results */}
              {testResult && (
                <div className={`border rounded-lg shadow-sm p-6 ${testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                  <div className="flex items-start mb-4">
                    {testResult.success ? (
                      <svg className="w-6 h-6 text-green-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    ) : (
                      <svg className="w-6 h-6 text-red-600 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    )}
                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold ${testResult.success ? 'text-green-900' : 'text-red-900'}`}>
                        {testResult.success ? t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.ResultPassed') : t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.ResultFailed')}
                      </h3>
                      {testResult.errorMessage && (
                        <p className={`mt-2 text-sm ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                          {testResult.errorMessage}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Response Metrics */}
                  {testResult.success && (
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="bg-white border border-green-200 rounded-lg p-3">
                        <div className="text-xs text-gray-600 mb-1">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.ResponseTimeLabel')}</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {testResult.responseTimeMs}ms
                        </div>
                      </div>
                      <div className="bg-white border border-green-200 rounded-lg p-3">
                        <div className="text-xs text-gray-600 mb-1">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.BooksFoundLabel')}</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {testResult.entitlementsCount || 0}
                        </div>
                      </div>
                      <div className="bg-white border border-green-200 rounded-lg p-3">
                        <div className="text-xs text-gray-600 mb-1">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.StatusCodeLabel')}</div>
                        <div className="text-lg font-semibold text-gray-900">
                          {testResult.statusCode || 200}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Sample Content IDs */}
                  {testResult.success && testResult.sampleIsbn && testResult.sampleIsbn.length > 0 && (
                    <div className="mb-4 p-4 bg-white border border-green-200 rounded">
                      <h4 className="text-sm font-semibold text-gray-900 mb-2">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.SampleContentIdsTitle')}</h4>
                      <ul className="text-sm text-gray-700 space-y-1">
                        {testResult.sampleIsbn.map((id, index) => (
                          <li key={index} className="font-mono text-xs">
                            {id || t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.NoId')}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Raw Response */}
                  {testResult.rawResponse && (
                    <div className="p-4 bg-white border border-gray-200 rounded">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-semibold text-gray-900">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.RawResponseTitle')}</h4>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(testResult.rawResponse || '');
                          }}
                          className="text-xs text-primary-600 hover:text-primary-800"
                        >
                          {t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.CopyButton')}
                        </button>
                      </div>
                      <pre className="text-xs text-gray-700 whitespace-pre-wrap break-words font-mono max-h-96 overflow-y-auto">
                        {testResult.rawResponse}
                      </pre>
                    </div>
                  )}

                  {testResult.success && (
                    <div className="mt-4 p-4 bg-white border border-green-200 rounded">
                      <h4 className="text-sm font-semibold text-green-900 mb-2">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.SuccessTitle')}</h4>
                      <ul className="text-sm text-green-800 space-y-1 list-disc list-inside">
                        <li>{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.SuccessItem1')}</li>
                        <li>{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.SuccessItem2')}</li>
                        <li>{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.SuccessItem3')}</li>
                        <li>{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.SuccessItem4')}</li>
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right Column - Token Reference */}
            <div className="lg:col-span-1">
              {/* Available Tokens */}
              <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 sticky top-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.AvailableTokensTitle')}</h3>
                <p className="text-sm text-gray-600 mb-4">
                  {t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.AvailableTokensHint')}
                </p>
                <div className="space-y-3">
                  {availableTokens.map((item, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between mb-1">
                        <code className="text-xs font-mono text-primary-600 font-semibold">
                          {item.token}
                        </code>
                        <button
                          onClick={() => {
                            navigator.clipboard.writeText(item.token);
                          }}
                          className="text-xs text-gray-500 hover:text-gray-700"
                          title={t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.CopyButton')}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                      <div className="text-xs text-gray-600 mb-1">{item.description}</div>
                      <div className="text-xs font-mono text-gray-500 break-all">
                        → {String(item.value)}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Example Usage */}
                <div className="mt-6 p-4 bg-primary-50 border border-primary-200 rounded-lg">
                  <h4 className="text-sm font-semibold text-primary-900 mb-2">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.ExampleUrlTitle')}</h4>
                  <code className="text-xs text-primary-800 block break-all font-mono">
                    https://api.bookseller.com/v3/entitlements?email={'{userEmail}'}
                  </code>
                  <div className="mt-3 text-xs text-primary-700">
                    <strong>{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.ExampleUrlBecomesLabel')}</strong>
                    <div className="mt-1 font-mono break-all">
                      https://api.bookseller.com/v3/entitlements?email={user?.email || 'user@example.com'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TroubleshootingTitle')}</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="font-medium text-gray-900 mb-2">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.Tip401Title')}</h4>
              <p className="text-sm text-gray-700">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.Tip401Desc')}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.Tip404Title')}</h4>
              <p className="text-sm text-gray-700">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.Tip404Desc')}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TipParsingTitle')}</h4>
              <p className="text-sm text-gray-700">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TipParsingDesc')}</p>
            </div>
            <div>
              <h4 className="font-medium text-gray-900 mb-2">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TipTimeoutTitle')}</h4>
              <p className="text-sm text-gray-700">{t('App.Admin.BooksellerTest.BooksellerEntitlementTestPage.TipTimeoutDesc')}</p>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BooksellerEntitlementTestPage;
